// Reusable sx fragments for v2 components.
// Most styling flows through the MUI theme; these are the spots where the
// design uses a recurring shape that's easier to drop in than to re-derive
// from scratch (chips, icon-only buttons, raised cards, monospace meta lines).
//
// Each fragment is a theme callback so it reads palette/typography from the
// active theme (no parallel token import). `<Box sx={v2Chip} />` still works
// because MUI's sx accepts function form.

import type { SxProps, Theme } from '@mui/material/styles';

export const v2Chip: SxProps<Theme> = (theme) => ({
    backgroundColor: theme.palette.surface.raised,
    border: `1px solid ${theme.palette.border.default}`,
    color: theme.palette.text.secondary,
    fontSize: 11,
    px: 1.25,
    py: 0.5,
    borderRadius: 999,
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    lineHeight: 1.2,
    transition: 'background-color .12s, color .12s',
    '&:hover': { backgroundColor: theme.palette.surface.hover, color: theme.palette.text.primary },
});

export const v2IconBtn: SxProps<Theme> = (theme) => ({
    background: 'transparent',
    border: 'none',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    p: 0.75,
    borderRadius: `${theme.shape.radiusSm}px`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': { backgroundColor: theme.palette.surface.raised, color: theme.palette.text.primary },
});

export const v2Card: SxProps<Theme> = (theme) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.default}`,
    borderRadius: `${theme.shape.borderRadius}px`,
});

export const v2MonoMeta: SxProps<Theme> = (theme) => ({
    fontFamily: theme.typography.fontFamilyMono,
    fontSize: 10.5,
    color: theme.palette.text.disabled,
});

export const v2SectionLabel: SxProps<Theme> = (theme) => ({
    fontSize: 10,
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    px: 1,
    py: 0.75,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
});
